/**
 * The importer's strings (piece 31; decisions 0920 to 0922), kept beside its
 * rules and spread into the one lookup in `strings.ts`, so `t()` stays the
 * only way a screen reads text. 🔨 This file imports nothing, because the
 * lookup imports it.
 */
export const IMPORT_STRINGS = {
  // 🔒 The Import page's help window (T435; decision 1103): seven topics, written into piece 31's *The help window* first and held to it by `topics.test.ts`.
  'help.importGuide.title': 'How the Zoho Books import works',
  'help.importGuide.open': 'How this works',
  'help.importBlocks.title': 'The four blocks, in order',
  'help.importBlocks.lead1': 'The Zoho Books import brings the old books in as four blocks, read and written one at a time in the order the page lists them.',
  'help.importBlocks.lead2': 'Block 1 is the chart of accounts and the taxes. Each Zoho account becomes one of our accounts, matched by its four-digit number or its name, or created from its type. Each Zoho tax is matched to one of our tax rows by name and rate, and a tax with no match is listed for you to place by hand.',
  'help.importBlocks.lead3': 'Block 2 is the contacts. Each Zoho contact becomes a company carrying its customer or vendor flag. Its billing and shipping addresses become the company’s sites, and each contact person becomes a person employed there.',
  'help.importBlocks.lead4': 'Block 3 is the items. Each Zoho item becomes an item in our catalog, matched on its manufacturer and part number or on its SKU, and its sales and purchase accounts are found through block 1.',
  'help.importBlocks.lead5': 'Block 4 is the documents dated inside a range, in the order of a sale: estimates, sales orders, invoices, credit notes, customer payments, purchase orders, bills, vendor credits, vendor payments, expenses and manual journals. An estimate becomes a quote here, a sales order and a purchase order keep their names, and the three post nothing. Every other document becomes the same document here and posts as Zoho posted it, and an expense becomes a manual journal. Zoho Inventory’s adjustments come last, each line a stock movement on its item that posts nothing.',
  'help.importBlocks.lead6': 'A Zoho draft stays a draft here and posts nothing, and a void document is not written. Beside the four blocks the page has three steps: Connect Zoho Books, Compare the reports for a written range, and Sign off, which closes the import once the Uncategorized report is acknowledged.',
  'help.importRead.title': 'Read, then write',
  'help.importRead.lead1': 'Read reads the block, or the narrowing you chose, from Zoho Books once. It lists every row it would create, update, leave unchanged and reject, and it writes nothing.',
  'help.importRead.lead2': 'The block’s row then offers Write all, Step through and Refresh. Write all writes every row the read listed, from the read itself, and asks Zoho Books for nothing.',
  'help.importRead.lead3': 'Write all never writes a contact or item row a rule matched to a record here, whatever the match’s grade, or a row whose Accept is held (decision 7551). It leaves each undecided for Step through, the read stays open, and its result counts the rows it left and why.',
  'help.importRead.lead4': 'A rejected row is never written, and it stays listed with its reason. The row says when the read was made, and Refresh reads the block again if Zoho Books has changed since.',
  'help.importRead.lead5': 'The blocks depend on each other. The contacts need no other block, and the items need the chart of accounts and taxes written first. The documents need both the contacts and the items written first.',
  'help.importRead.lead6': 'A record somebody changed here since the last write is kept and listed among the rejected rows. Tick Overwrite local changes before Read to replace it with Zoho’s copy instead.',
  'help.importScopes.title': 'The three ways to narrow a run',
  'help.importScopes.lead1': 'A run reads its whole block unless you narrow it in the block’s own row. The row offers three ways, and a blank one reads everything.',
  'help.importScopes.lead2': 'Kind reads one kind of record inside the block, such as only the taxes in block 1 or only the bills in block 4.',
  'help.importScopes.lead3': 'One record, by Zoho id, reads a single record of the kind chosen. Choose the kind first, then type the record’s id from Zoho Books.',
  'help.importScopes.lead4': 'A date range reads records by date. In block 4 that is each document’s own date. In blocks 1 to 3 it is the day Zoho last changed the record, so a record changed outside the range is not read.',
  'help.importScopes.lead5': 'Write all and Step through write only what the narrowed read listed. The rest of the block waits for a read of its own.',
  'help.importDateRange.title': 'Writing every record in a date range',
  'help.importDateRange.lead1': 'First write blocks 1, 2 and 3 whole, with their kind, record and dates left blank.',
  'help.importDateRange.lead2': 'Then, in the documents block, choose the range’s first and last day in From and To, and leave Kind at Every kind. Press Read, look over the rows, then press Write all.',
  'help.importDateRange.lead3': 'The first documents read lists the opening balances as its first row, from Zoho’s balance sheet the day before From. Writing that row opens the ledger, and From becomes the go-live date.',
  'help.importDateRange.lead4': 'A later range cannot start before the go-live date, and cannot leave a gap after the days already written. The From button beside the range fills in the next day to read.',
  'help.importDateRange.lead5': 'Once a range is written, Compare the reports sets our Profit and Loss and Balance Sheet beside Zoho’s for that range, section by section.',
  'help.importStepped.title': 'Stepping through a block',
  'help.importStepped.lead1': 'Step through opens the rows a read listed, one at a time, and writes nothing until you decide. Only a row you accept is written.',
  'help.importStepped.lead2': 'Each row shows the record here beside the incoming one, field by field. Accept writes it, Skip leaves it out, Edit lets you change its typed fields first, and Merge writes it into a record you pick.',
  'help.importStepped.lead3': 'A box beside each field says whether Accept writes it, and the Accept column says what each field does: Update, Create, Keep or Same. A field already filled here starts cleared.',
  'help.importStepped.lead4': 'Write all writes every row still undecided, so you can step the rows that worry you and write the rest at once. Close run frees the block and leaves any undecided row unwritten.',
  'help.importStepped.lead5': 'Use it for a first read of the contacts or the items, where a wrong match makes a second company or a second item.',
  'help.importStepped.lead6': 'A documents read lists its opening balances first, and every document waits on that row until the ledger is open. Each document offers Accept and Skip alone.',
  'help.importCancel.title': 'Canceling a read or a write',
  'help.importCancel.lead1': 'A running read shows its progress page by page, and Cancel stops it after the page it is reading. A canceled read writes nothing.',
  'help.importCancel.lead2': 'A canceled Write all keeps every record it wrote. Press Write all again to finish: the read still stands, and Zoho Books is not asked again.',
  'help.importCancel.lead3': 'A row you accepted in Step through was written when you accepted it, and no cancel undoes it.',
  'help.importResults.title': 'Where the results land',
  'help.importResults.lead1': 'Each run’s result opens inside its block’s step, and Show result on a block opens its last one again. It counts the rows to create, to update, unchanged and rejected, and the rows written so far.',
  'help.importResults.lead2': 'Rejected rows lists every row that will not be written, with its reason. Show every row adds the rest, each with its notes, such as an account matched by name rather than by number.',
  'help.importResults.lead3': 'Open the report opens the run’s full list as a file, kept on our own company’s record.',
  'help.importResults.lead4': 'An item whose unit matches none here is listed under Units to match. Match the unit once, and the read’s undecided rows carrying it convert again.',
  'help.importResults.lead5': 'A closed run leaves a cleanup list of its Weak matches and the rows accepted with edits, merged, skipped or never decided. Its block’s row counts the entries, and Open the list shows each with Revisit.',
  'help.importResults.lead6': 'To decide every row a closed run left, read the same range again from the block’s row. The rows already written read as unchanged, and Step through walks the rest.',
  'zohoBooks.dc.com': 'United States (zoho.com)',
  'zohoBooks.dc.eu': 'Europe (zoho.eu)',
  'zohoBooks.dc.in': 'India (zoho.in)',
  'zohoBooks.dc.comAu': 'Australia (zoho.com.au)',
  'zohoBooks.dc.jp': 'Japan (zoho.jp)',
  'zohoBooks.dc.ca': 'Canada (zohocloud.ca)',
  'zohoBooks.dc.sa': 'Saudi Arabia (zoho.sa)',
  // 🔨 T3723: a read whose network failed three times ends with this sentence on the run, not a bare fault.
  'import.zoho.unreachable': 'Zoho could not be reached, so nothing was read. Try the read again in a few minutes.',
  // 🔒 Decision 5981 (T2746): the match log's two acts on a run's page.
  'import.matchLog.title': 'Match log',
  'import.matchLog.hint': 'One row for every row this run read: the match the import proposed, what you decided and what was written. A sealed field is named and its value is never in it; an edit shows what you typed.',
  'import.matchLog.download': 'Download Match Log',
  'import.matchLog.copy': 'Copy for Claude',
  'import.matchLog.busy': 'The match log is being read.',
  'import.matchLog.copied': 'The match log is copied. Paste it into Claude.',
  'import.matchLog.copyFailed': 'The match log was not copied.',
  'import.matchLog.downloadFailed': 'The match log was not downloaded.',
  // 🔒 Decision 13070 (T3894): the Compare Reports download on a run's page and on Step Through's selected row.
  'import.compareReports.title': 'Compare Reports',
  'import.compareReports.runHint': 'One line for every field of every record this run read: the value Zoho sent beside the value written here, and whether the two differ. Unlike the match log it carries the values themselves, so Copy for Claude never pastes it; attach the file to Claude yourself.',
  'import.compareReports.rowHint': 'Compare Reports for this record alone: each value Zoho sent beside the value written here, in one file.',
  'import.compareReports.download': 'Download Compare Reports',
  'import.compareReports.busy': 'The comparison is being read.',
  'import.compareReports.failed': 'The comparison was not downloaded.',
  'import.cleanup.title': 'Cleanup list',
  'import.cleanup.hint': 'What this run left uncertain: its Weak matches, the rows accepted with edits, merged, skipped and never decided, and the placeholder sites its writes opened. Revisit reads that one record from Zoho Books again as a read of its own. To decide every row it left, read the same range or scope again from the block’s row: the rows already written read as unchanged, and Step through walks the rest.',
  'import.cleanup.none': 'Nothing is left uncertain on this run.',
  'import.cleanup.revisit': 'Revisit',
  'import.cleanup.pickOne': 'Tick one row to revisit; each is read again as a run of its own.',
  'import.cleanup.revisited': 'Revisited',
  'import.cleanup.onCard': 'Cleanup list of the last closed run:',
  'import.cleanup.open': 'Open the list',
  'import.cleanup.pair': 'Kept apart: the company here',
  'import.cleanup.pairAnd': 'Kept apart: the company created',
  'import.cleanup.pairHint': 'Two companies here now, kept apart as chosen. If they turn out to be one, the duplicate review opens at their pair and merges them; this list never merges.',
  'import.cleanup.review': 'Duplicate review',
  'import.cleanup.reviewOpen': 'Review as duplicates',
  'import.cleanup.category.weak': 'Weak matches',
  'import.cleanup.category.edited': 'Accepted with edits',
  'import.cleanup.category.merged': 'Merged',
  'import.cleanup.category.skipped': 'Skipped',
  'import.cleanup.category.undecided': 'Never decided',
  'import.cleanup.category.placeholderSite': 'Placeholder sites',
  // Decision 11661: an imported invoice held as a draft because our tax differs from Zoho Books', and its two acts.
  'import.cleanup.category.taxHold': 'Tax to decide',
  'import.cleanup.ourTax': 'Our tax',
  'import.cleanup.zohoTax': 'Zoho Books’ tax',
  'import.cleanup.taxHint': 'An invoice whose tax worked out here differs from the tax Zoho Books charged is held as a draft and posts nothing. Tick it, then Keep the Calculation issues it with our tax, or Use Zoho’s Tax issues it with Zoho Books’ amount.',
  'import.cleanup.keepTax': 'Keep the Calculation',
  'import.cleanup.useZohoTax': 'Use Zoho’s Tax',
  'import.cleanup.pickTaxHold': 'Tick one invoice held for its tax.',
  'import.cleanup.invoice': 'Held invoice',
  'import.cleanup.invoiceOpen': 'Open the invoice',
  'import.cleanup.keepTaxConfirm': 'This issues the held invoice with the tax our rates worked out and posts it, and an issued invoice is not edited.',
  'import.cleanup.useZohoTaxConfirm': 'This issues the held invoice with the tax Zoho Books charged in place of ours and posts it, and an issued invoice is not edited.',
  'import.cleanup.placeholder': 'Add the real address on the company',
  'import.cleanup.placeholderHint': 'A placeholder site was opened, with no address, for each customer Zoho Books gave no billing address. Its invoices carry no ship-to and are taxed at nothing; open the company and add the real address.',
  'import.cleanup.placeholderNoRevisit': 'A placeholder site is mended on its company: open the company and add the real address. Reading the document again finds the same missing address.',
  // Decision 3262: a cleanup entry the read itself rejected, beside its reason.
  'import.cleanup.rejectedByRead': 'Rejected by the read:',
  'import.stepThrough': 'Step Through',
  'import.staged.title': 'Waiting on decisions',
  'import.stepped.undecided': 'undecided of',
  'import.stepped.rows': 'rows',
  'import.stepped.filterUndecided': 'Undecided only',
  'import.stepped.acceptSelected': 'Accept Selected',
  'import.stepped.skipSelected': 'Skip Selected',
  'import.stepped.pickRows': 'Tick the rows to decide first.',
  'import.stepped.convertAgain': 'Convert Again',
  'import.stepped.convertAgainHint': 'Converts the undecided rows again with the import mappings as they now stand; a decided row keeps its values.',
  'import.stepped.close': 'Close Run',
  'import.stepped.closeHint': 'Closes the run: undecided rows stay unwritten, the block is free for its next read, and the run’s report is written.',
  'import.stepped.previous': 'Previous',
  'import.stepped.next': 'Next',
  'import.stepped.noRows': 'No rows to show.',
  'import.stepped.pickRow': 'Choose a row to decide it.',
  'import.stepped.col.pick': 'Pick',
  'import.stepped.col.row': 'Row',
  'import.stepped.col.proposal': 'Proposed',
  'import.stepped.col.grade': 'Match',
  'import.stepped.col.flags': 'Flags',
  'import.stepped.col.decision': 'Decision',
  'import.stepped.col.field': 'Field',
  'import.stepped.col.here': 'Here Now',
  /** 🔒 Decision 8160: the Here Now column's head on a join to the company a sibling row creates. */
  'import.stepped.col.hereSibling': 'From Row {row}',
  'import.stepped.col.incoming': 'Incoming, Converted',
  'import.stepped.col.typed': 'Typed',
  'import.stepped.col.take': 'Take',
  // Decision 3084 (T2238): the box beside each field Accept writes, and what Accept does with it.
  'import.stepped.col.write': 'Write',
  'import.stepped.col.accept': 'Accept',
  'import.stepped.acceptWord.update': 'Update',
  'import.stepped.acceptWord.create': 'Create',
  'import.stepped.acceptWord.keep': 'Keep',
  'import.stepped.acceptWord.same': 'Same',
  // T2739 (decisions 5382, 5631): the study's Suggested column, its boxes and its lines.
  'import.stepped.col.suggested': 'Suggested',
  'import.stepped.acceptWord.suggested': 'Suggested',
  'import.stepped.takeSuggestion': 'Take the suggestion for',
  'import.study.checking': 'Checking',
  'import.study.people': 'Suggested for the people',
  'import.count.notCheckedCap': "rows not checked at the month's spending cap",
  'import.stepped.writeField': 'Write the incoming',
  'import.stepped.wrote': 'wrote',
  'import.stepped.proposal.create': 'Create a new record',
  'import.stepped.proposal.update': 'Update the record here',
  'import.stepped.proposal.unchanged': 'Unchanged',
  'import.stepped.proposal.reject': 'Would be rejected',
  'import.stepped.grade.exact': 'Exact',
  'import.stepped.grade.weak': 'Weak',
  'import.stepped.grade.noMatch': 'No match',
  'import.stepped.decision.accept': 'Accepted',
  'import.stepped.decision.skip': 'Skipped',
  'import.stepped.decision.edit': 'Accepted with edits',
  'import.stepped.decision.merge': 'Merged',
  'import.stepped.refused': 'Refused:',
  'import.stepped.matched': 'Matches',
  'import.stepped.setAside': 'Set aside:',
  // Decision 2141: a rejected row that read nothing carries no field to accept, edit or merge.
  'import.stepped.nothingRead': 'This row is refused and carries no fields to accept, edit or merge; Skip leaves it out.',
  'import.stepped.changedHere': 'The record here was changed since the last write.',
  // Decision 7410: a row changed here beside its grey Accept names the three ways forward.
  'import.stepped.changedHereWays': 'Skip keeps the record here. Merge takes the fields you tick. Overwrite local changes on a new run takes Zoho’s copy.',
  'import.stepped.differs': 'Differs',
  'import.stepped.accept': 'Accept',
  'import.stepped.skip': 'Skip',
  'import.stepped.joinInstead': 'Join Instead:',
  'import.stepped.edit': 'Edit',
  'import.stepped.merge': 'Merge',
  'import.stepped.notThisOne': 'Not This One',
  'import.stepped.reopen': 'Reopen',
  'import.stepped.remember': 'Do the same for every undecided row like this one',
  'import.stepped.acceptEdits': 'Accept with These Values',
  'import.stepped.cancelEdit': 'Stop Editing',
  'import.stepped.mergeSearch': 'Record here to merge into',
  'import.stepped.mergeFind': 'Find',
  'import.stepped.mergePick': 'Choose the record',
  'import.stepped.mergeDo': 'Merge into It',
  'import.stepped.mergeNeedsTarget': 'Choose the record to merge into first.',
  'import.stepped.takeHere': 'Here',
  'import.stepped.takeIncoming': 'Incoming',
  'import.stepped.decidedBy': 'Decided by',
  'import.stepped.followed': 'Followed row',
  'import.stepped.mergedInto': 'Merged into',
  'import.stepped.unitHint': 'Its unit matches none here. Match it once, and every undecided row carrying it converts again.',
  'import.stepped.followedCount': 'more rows of the same shape followed.',
  'import.stepped.bulkDone': 'rows decided.',
  'import.stepped.closed': 'The run is closed.',
  // 🔒 Decision 7551: a contact or item row a rule matched waits for a person, and says what Accept writes.
  'import.stepped.matchWaits': 'Write all leaves this match for you. Accept merges the row into',
  'import.stepped.matchWaitsTail': 'and writes the fields ticked; Not This One keeps both, and Skip leaves it out.',
  // 🔒 Decision 8000: a credit limit in another currency than the company here's is shown in its own and not brought over; an Edit or a Merge of it is refused, and a typed limit is a plain figure within the column.
  'import.stepped.creditLimitForeign': 'The credit limit is in',
  'import.stepped.creditLimitForeignTail': 'and is not brought over',
  'import.stepped.edit.notNumber': 'The field {field} takes a number.',
  'import.stepped.edit.limitNegative': 'The credit limit takes zero or more.',
  'import.stepped.edit.limitTooBig': 'The credit limit takes at most 21,474,836.47 dollars.',
  'import.stepped.edit.limitForeign': 'This contact’s credit limit is in another currency than the company here’s, so it is not brought over and neither an Edit nor a Merge takes it.',
  // 🔒 Decision 7940: a contact row a rule matched is an Associate, which adds the link, the sites and the people and writes no field of the company.
  'import.stepped.associateWaits': 'Write all leaves this match for you. Accept associates the row with',
  'import.stepped.associateWaitsTail': 'and writes none of its fields but a missing flag; Merge takes the fields you pick, Not This One keeps both, and Skip leaves it out.',
  'import.stepped.proposal.associate': 'Associate with a company',
  'import.stepped.associate.head': 'Associate with',
  'import.stepped.associate.planned': 'which this run creates',
  'import.stepped.associate.tail': 'adds this row’s addresses as sites, employs its people there and adds its customer or vendor flag, writing none of its other fields.',
  'import.stepped.associate.adds': 'Adds',
  'import.stepped.associate.employs': 'employs',
  'import.stepped.associate.noSite': 'Adds no site',
  'import.stepped.associate.nobody': 'employs nobody',
  // 🔒 Decision 7940 (review of T3182): a join to a company a sibling row creates waits for that row, and never falls back to a create.
  'import.stepped.associate.siblingFirst': 'Write row {row}, {name}, first: this row joins the company it creates.',
  'import.stepped.associate.siblingSkipped': 'Its company row, row {row}, {name}, was skipped, so this row creates its own company.',
  'import.stepped.associate.siblingMerged': 'Its company row, row {row}, {name}, was merged into a company here, so this row creates its own company.',
  'import.stepped.associate.noCompany': 'The company this row associates with is not here, so nothing was written; Convert Again reads the row afresh.',
  'import.stepped.associate.decided': 'Associated',
  'import.stepped.associate.decidedEdits': 'Associated with edits',
  'import.stepped.associate.editHint': 'Edit types the sites and the people this row adds. The company’s own fields change through Merge, or on its page.',
  'import.stepped.associate.role.billing': 'Billing address',
  'import.stepped.associate.role.shipping': 'Shipping address',
  'import.stepped.associate.field.line1': 'Street',
  'import.stepped.associate.field.city': 'City',
  'import.stepped.associate.field.state': 'State',
  'import.stepped.associate.field.postalCode': 'Postal code',
  'import.stepped.associate.field.firstName': 'First name',
  'import.stepped.associate.field.lastName': 'Last name',
  'import.stepped.associate.field.email': 'Email',
  'import.stepped.associate.field.phone': 'Phone',
  // 🔒 Decision 8100: a contact person's department, typed over Zoho's on an Associate row.
  'import.stepped.associate.field.department': 'Department',
  'import.field.accountNumber': 'Account number',
  'import.field.name': 'Name',
  'import.field.type': 'Type',
  'import.field.detailType': 'Subtype',
  'import.field.cashFlowSection': 'Cash flow',
  'import.field.rate': 'Rate',
  'import.field.displayName': 'Company name',
  'import.field.isCustomer': 'Customer',
  'import.field.isVendor': 'Vendor',
  'import.field.website': 'Website',
  'import.field.mainPhoneNumber': 'Main phone',
  'import.field.paymentTerms': 'Payment terms',
  'import.field.creditLimit': 'Credit limit',
  'import.field.currencyCode': 'Currency',
  // 🔒 Decision 3082: a contact's two sealed numbers, read On file or blank on the compare and never as digits.
  'import.field.ein': 'Tax ID',
  'import.field.dunsNumber': 'DUNS number',
  'import.field.sku': 'SKU',
  'import.field.manufacturerCompanyId': 'Manufacturer',
  'import.field.manufacturerPartNumber': 'Manufacturer part number',
  'import.field.unitId': 'Unit',
  'import.field.salesAccountId': 'Sales account',
  'import.field.purchaseAccountId': 'Purchase account',
  'import.field.salesPrice': 'Sales price',
  'import.field.purchasePrice': 'Purchase price',
  'import.field.listPrice': 'List price',
  'import.field.salesDescription': 'Sales description',
  'import.field.purchaseDescription': 'Purchase description',
  'import.field.status': 'Status',
  'import.field.categoryId': 'Category',
  'import.field.taxCategoryId': 'Tax category',
  'import.field.supplierCompanyId': 'Supplier',
  'import.field.attributes': 'Attributes',
  'import.field.number': 'Number',
  'import.field.date': 'Date',
  'import.field.contact': 'Contact',
  'import.field.total': 'Total',
  'import.field.lines': 'Lines',
  'import.field.reference': 'Reference',
  'settings.importMappings.title': 'Import Mappings',
  'settings.importMappings.hint': 'How records from Zoho Books are brought in: which of our account types each Zoho account type becomes, how an item’s fields carry across, and how a record is recognized as one we already have. A change applies to the next import.',
  'settings.importMappings.accountTypes': 'Account types',
  'settings.importMappings.accountTypesHint': 'Which of our account types, subtypes and cash flow sections each Zoho account type becomes, and the number range for an account Zoho gives no four-digit number. A type turned off is refused at import.',
  'settings.importMappings.fields': 'Item fields',
  'settings.importMappings.fieldsHint': 'Which field here each field of a Zoho item fills, and how its value is converted. A line turned off fills nothing, and the value already here stands.',
  'settings.importMappings.rules': 'Matching rules',
  'settings.importMappings.rulesHint': 'How an imported record is recognized as one already here, trying each kind of record’s rules from the top. A rule turned off matches nothing. Every match is a proposal: Write all leaves it for a person in Step through, and the grade only ranks it.',
  'settings.importMappings.col.type': 'Type',
  'settings.importMappings.col.subtype': 'Subtype',
  'settings.importMappings.col.cashFlow': 'Cash Flow',
  'settings.importMappings.col.bandFrom': 'Band From',
  'settings.importMappings.col.bandTo': 'Band To',
  'settings.importMappings.band': 'Numbers {from}–{to}',
  'settings.importMappings.col.on': 'On',
  'settings.importMappings.off': 'Off',
  'settings.importMappings.col.zohoField': 'Zoho Item Field',
  'settings.importMappings.col.itemField': 'Item Field Here',
  'settings.importMappings.col.conversion': 'Conversion',
  'settings.importMappings.col.kind': 'Record',
  'settings.importMappings.col.key': 'Matched On',
  'settings.importMappings.col.grade': 'Grade',
  'settings.importMappings.col.order': 'Order',
  'settings.importMappings.moveUp': 'Up',
  'settings.importMappings.moveDown': 'Down',
  'settings.importMappings.first': 'Already tried first.',
  'settings.importMappings.last': 'Already tried last.',
  'settings.importMappings.edited': 'Last edited by',
  'settings.importMappings.saved': 'Saved; the next run reads it.',
  'settings.importMappings.openImport': 'Open Import',
  'settings.importMappings.attachments': 'Attachments',
  'settings.importMappings.attachmentsHint': 'Which file type the files of each Zoho Books document type are filed under when an import brings them, and whether they are confidential. Each document type is asked once, never once per file. A type with no answer brings its files in confidential and unclassified, into Needs classification. An answer reaches the next run.',
  'settings.importMappings.col.documentType': 'Zoho Document',
  'settings.importMappings.col.fileType': 'File Type',
  'settings.importMappings.col.confidential': 'Confidential',
  'settings.importMappings.col.answeredBy': 'Answered By',
  'settings.importMappings.notAnswered': 'Not answered: confidential and unclassified',
  'settings.importMappings.noFileType': 'No active file type applies to this document; add one in Settings › Documents › File Types first.',
  'settings.importMappings.itemDetails': 'Item details',
  'settings.importMappings.itemDetailsHint': 'Each detail the last items read found on a Zoho item that no field row reads: its custom fields, brand, codes, category name, item group options, tax and preferred vendor. Each is asked about once, never once per item. Map it to an item field the item already has, such as its list price, its tax category or its preferred vendor; or create a list attribute of the catalog named after it, with every value seen as one of its choices; or skip it. An answer is changed later by choosing another target.',
  'settings.importMappings.itemDetailsNone': 'No items read has run yet, so no detail is listed. Read the items block on the Import screen first.',
  'settings.importMappings.col.detail': 'Zoho Detail',
  'settings.importMappings.col.items': 'Items',
  'settings.importMappings.col.mapsTo': 'Maps To',
  'settings.importMappings.col.values': 'Values Seen',
  'settings.importMappings.ask': 'Create the Attribute',
  'settings.importMappings.noValues': 'The last read saw no value for this detail, so there is no choice to create.',
  'settings.importMappings.saving': 'Saving.',
  'settings.importMappings.attribute': 'Attribute',
  'settings.importMappings.notMapped': 'Not mapped',
  'settings.importMappings.target.ignore': 'Ignore',
  'settings.importMappings.target.category': 'Category',
  'settings.importMappings.target.attribute': 'Attribute',
  'settings.importMappings.target.taxCategory': 'Tax category',
  'settings.importMappings.target.supplier': 'Supplier',
  // Decision 1921: the first question's three ways, and the item fields its picker offers.
  'settings.importMappings.target.itemField': 'List price (item field)',
  'settings.importMappings.mapToField': 'Map to an item field',
  'settings.importMappings.skip': 'Skip',
  'settings.importMappings.field.listPrice': 'List price',
  'settings.importMappings.field.taxCategory': 'Tax category',
  'settings.importMappings.field.preferredVendor': 'Preferred vendor',
  'settings.importMappings.categoryRules': 'Category rules',
  'settings.importMappings.categoryRulesHint': 'Tried in this order; the first rule an item’s details meet names its category. An item no rule names stays in Uncategorized.',
  'settings.importMappings.when': 'When',
  'settings.importMappings.is': 'is',
  'settings.importMappings.thenCategory': 'category',
  'import.grade.exact': 'Exact',
  'import.grade.weak': 'Weak',
  'import.key.accountNumber': 'Account number',
  // Decision 7840: the name matches on the account's own side.
  'import.key.accountName': 'Account name and side',
  'import.key.taxNameAndRate': 'Tax name and rate',
  'import.key.ein': 'Tax ID',
  // Decisions 6332, 7880: a contact's website domain, outside the free-mail list; a person's email domain is no longer a key.
  'import.key.emailDomain': 'Website domain',
  'import.key.nameAndPostalCode': 'Name and postal code',
  'import.key.workEmail': 'Work email',
  'import.key.nameAtCompany': 'Full name at the same company',
  'import.key.manufacturerAndPartNumber': 'Manufacturer and part number',
  'import.key.sku': 'SKU alone',
  'import.key.documentNumber': 'Document number',
  'import.key.vendorAndNumber': 'Vendor and bill number',
  'import.key.paymentParticulars': 'Company, reference, date and amount',
  'import.key.referenceAndDate': 'Reference and date',
  // Decision 12360: a conversion rule, not a match key.
  'import.key.cashModeCheck': 'A Zoho payment at mode Cash whose reference is a whole number is a check, with that number as its check number',
  'import.ruleKind.account': 'Account',
  'import.ruleKind.tax': 'Tax',
  'import.ruleKind.contact': 'Contact’s company',
  'import.ruleKind.contactPerson': 'Contact person',
  'import.ruleKind.item': 'Item',
  'import.ruleKind.estimate': 'Estimate',
  'import.ruleKind.salesOrder': 'Sales order',
  'import.ruleKind.invoice': 'Invoice',
  'import.ruleKind.creditNote': 'Credit note',
  'import.ruleKind.customerPayment': 'Customer payment',
  'import.ruleKind.purchaseOrder': 'Purchase order',
  'import.ruleKind.bill': 'Bill',
  'import.ruleKind.vendorCredit': 'Vendor credit',
  'import.ruleKind.vendorPayment': 'Vendor payment',
  'import.ruleKind.expense': 'Expense',
  'import.ruleKind.manualJournal': 'Manual journal',
  'import.conversion.copy': 'Copied as it is',
  'import.conversion.centsToDollars': 'Cents to dollars',
  'import.conversion.unitRule': 'The unit match',
  'import.conversion.manufacturerByName': 'Manufacturer by name',
  'import.conversion.accountBySourceRef': 'The account block 1 linked',
  'import.conversion.activeToStatus': 'Active to status',
  'import.conversion.serviceToType': 'Service to type',
  'import.seamField.name': 'Name',
  'import.seamField.sku': 'SKU',
  'import.seamField.partNumber': 'Part number',
  'import.seamField.manufacturer': 'Manufacturer',
  'import.seamField.description': 'Sales description',
  'import.seamField.purchaseDescription': 'Purchase description',
  'import.seamField.rateCents': 'Rate',
  'import.seamField.purchaseRateCents': 'Purchase rate',
  'import.seamField.unit': 'Unit',
  'import.seamField.isService': 'Service or goods',
  'import.seamField.isActive': 'Active',
  'import.seamField.salesAccountId': 'Sales account',
  'import.seamField.purchaseAccountId': 'Purchase account',
  'import.itemField.name': 'Name',
  'import.itemField.sku': 'SKU',
  'import.itemField.manufacturerPartNumber': 'Manufacturer part number',
  'import.itemField.manufacturerCompanyId': 'Manufacturer',
  'import.itemField.salesDescription': 'Sales description',
  'import.itemField.purchaseDescription': 'Purchase description',
  'import.itemField.salesPrice': 'Sales price',
  'import.itemField.purchasePrice': 'Purchase price',
  'import.itemField.listPrice': 'List price',
  'import.itemField.unitId': 'Unit',
  'import.itemField.salesAccountId': 'Sales account',
  'import.itemField.purchaseAccountId': 'Purchase account',
  'import.itemField.status': 'Status',
  'import.itemField.type': 'Type',
  'import.title': 'Import',
  'import.hint': 'Load records from another system, one preset at a time. Every run reads first, lists what it would create, update and reject, and writes only when you say so. Its report is kept as a file on our own company.',
  'import.preset.legend': 'Importer',
  'import.preset.zohoBooks': 'Zoho Books',
  'import.preset.priceList': 'Vendor Price List',
  // 🔒 Decision 8380: the Zoho CRM preset, its connection and its Manufacturers block.
  'import.preset.zohoCrm': 'Zoho CRM',
  'import.preset.zohoExpense': 'Zoho Expense',
  'import.block.manufacturers': 'Manufacturers',
  'import.block.expenseReports': 'Expense Reports',
  'import.block.expenseReportsHint': 'Zoho Expense’s reports from the day the ledger opens, each with its expenses and their receipts; nothing posts, and each line links to the journal the Books pull wrote.',
  'import.block.manufacturersHint': 'The CRM’s manufacturers module as companies flagged Manufacturer, each with its address as a site and its contacts as people.',
  'import.crm.hint': 'Zoho CRM uses the client, the client secret and the data center saved on Settings › Integrations › Zoho Books, with a connection of its own.',
  'import.crm.steps.connect': 'Connect Zoho CRM',
  'import.crm.connect': 'Connect with Zoho CRM',
  'import.crm.reconnect': 'Connect Again',
  'import.crm.disconnect': 'Disconnect',
  'import.crm.chooseModule': 'Choose the Module',
  'import.crm.module': 'Manufacturers Module',
  'import.crm.modulePick': 'Choose one of the custom modules Zoho CRM lists.',
  'import.crm.moduleSave': 'Save Module',
  'import.crm.moduleSaved': 'The manufacturers module is saved.',
  'import.crm.noModule': 'No manufacturers module is chosen yet; connect, or choose the module.',
  'import.crm.redirect': 'Register this redirect address in Zoho’s console beside the Books one:',
  'import.crm.needsClient': 'Save the client ID, the client secret and the data center on Settings › Integrations › Zoho Books first.',
  'import.crm.notConnected': 'Zoho CRM is not connected; press Connect with Zoho CRM.',
  'import.crm.connectFirst': 'Connect Zoho CRM and keep its manufacturers module first.',
  'import.crm.connected': 'Zoho CRM is connected.',
  'import.crm.refused': 'Zoho CRM was not connected:',
  'import.crm.disconnected': 'Zoho CRM is disconnected.',
  'import.crm.disconnectUnconfirmed': 'Zoho CRM is disconnected here, but Zoho did not confirm the revoke.',
  'import.crm.source.zoho': 'Reading Zoho CRM.',
  'import.crm.source.sample': 'Reading the sample CRM, since Zoho CRM is not connected on this machine.',
  'import.crm.source.none': 'Zoho CRM is not connected.',
  'import.expense.hint': 'Zoho Expense uses the client, the client secret and the data center saved on Settings › Integrations › Zoho Books, with a connection of its own.',
  'import.expense.steps.connect': 'Connect Zoho Expense',
  'import.expense.connect': 'Connect with Zoho Expense',
  'import.expense.reconnect': 'Connect Again',
  'import.expense.disconnect': 'Disconnect',
  'import.expense.redirect': 'Register this redirect address in Zoho’s console beside the Books one:',
  'import.expense.needsClient': 'Save the client ID, the client secret and the data center on Settings › Integrations › Zoho Books first.',
  'import.expense.notConnected': 'Zoho Expense is not connected; press Connect with Zoho Expense.',
  'import.expense.connectFirst': 'Connect Zoho Expense first.',
  'import.expense.needsDocuments': 'Write a documents range first; each expense line links to the journal the documents wrote.',
  'import.expense.cutoff': 'Reads the reports whose period ends on or after',
  'import.expense.noCutoff': 'the day the ledger opens, once a documents range is written',
  'import.expense.connected': 'Zoho Expense is connected.',
  'import.expense.refused': 'Zoho Expense was not connected:',
  'import.expense.disconnected': 'Zoho Expense is disconnected.',
  'import.expense.disconnectUnconfirmed': 'Zoho Expense is disconnected here, but Zoho did not confirm the revoke.',
  'import.expense.source.zoho': 'Reading Zoho Expense.',
  'import.expense.source.sample': 'Reading the sample expense reports, since Zoho Expense is not connected on this machine.',
  'import.expense.source.none': 'Zoho Expense is not connected.',
  'import.merge.itemMaker': 'merged on an item’s maker',
  'import.priceList.hint': 'A vendor’s price list is loaded from its own page under Settings › Catalog.',
  'import.priceList.open': 'Open the Price List Importer',
  'import.empty': 'Nothing to show yet.',
  'import.connection.zoho': 'Connected through Settings › Integrations › Zoho Books.',
  'import.connection.sample': 'No Zoho Books rows are held, so this machine answers with the sample books. Nothing here is real Zoho data.',
  'import.connection.none': 'Not connected. Rows held:',
  'import.connection.test': 'Test Connection',
  'import.connection.testing': 'Testing the connection…',
  'import.connection.needsRows': 'Enter the Zoho Books rows and press Connect with Zoho in Settings › Integrations › Zoho Books first.',
  'import.connection.settings': 'Zoho Books Settings',
  'import.connection.ok': 'Connected to',
  'import.connection.unnamed': 'an organization with no name',
  // 🔒 Decision 3800: sync mode's switch and its end date, on the Connect step beside the connection.
  'import.sync.title': 'Daily Pull from Zoho Books',
  'import.sync.switch': 'Daily pull',
  'import.sync.until': 'Last day it runs',
  'import.sync.hint': 'Once a day the import asks Zoho Books what changed since its last pull and writes it here. It stops after the end date.',
  'import.sync.save': 'Save Daily Pull',
  'import.sync.saved': 'The daily pull is saved.',
  'import.sync.savedCleared': 'The daily pull is saved, and its last day is cleared.',
  'import.sync.unchanged': 'Change the switch or the last day first, then save.',
  'import.sync.saving': 'Saving the daily pull…',
  'import.sync.onUntil': 'Daily pull on until',
  'import.sync.off': 'Daily pull off',
  'import.sync.lastPull': 'Last daily pull',
  'import.sync.neverPulled': 'No daily pull has run yet.',
  'import.sync.runMark': 'Daily pull',
  'import.sync.ended': 'Daily pull on, but its last day has passed, so nothing is pulled; it ended',
  // Decision 1760: the Zoho Books import as seven steps through Setup Steps.
  'import.steps.connect': 'Connect Zoho Books',
  'import.steps.writtenWhole': 'Written whole on',
  'import.steps.documents': 'Documents by range, with the opening balances',
  'import.steps.compare.checked': 'Checked for',
  'import.steps.compare.mark': 'Mark the Reports Checked',
  'import.steps.compare.markHint': 'Once you have read the comparison for the range above and accept every difference it shows, mark it checked. Writing a later range opens this step again for that range.',
  'import.steps.compare.readFirst': 'Press Compare with Zoho Books and read the comparison first.',
  'import.steps.compare.marking': 'Marking the reports checked…',
  'import.steps.compare.marked': 'The reports are marked checked for the range last written.',
  'import.steps.signOff': 'Sign off',
  'import.steps.signOff.waits': 'Mark the reports checked for the range last written first.',
  'import.steps.signOff.body': 'Go-live is signed off against the report of every item the import left in Uncategorized. Read it, work the items you can in Zoho Books, then acknowledge it.',
  'import.blocks.hint': 'Read one block at a time, in order, and check its reporting before the next. Read lists the rows and writes nothing; Write all or Step through then writes them.',
  'import.overwrite': 'Overwrite local changes',
  'import.overwriteHint': 'Unticked, a record somebody changed here since the last write is kept and listed among the rejected rows. Ticked, the next read proposes Zoho Books’ copy in its place.',
  'import.block.accountsAndTaxes': 'Chart of accounts and taxes',
  'import.block.accountsAndTaxesHint': 'Each Zoho account onto our chart by number or name, the rest created by type; each tax onto our tax rows by name and rate.',
  'import.block.contacts': 'Contacts',
  'import.block.contactsHint': 'Customers and vendors as companies, their addresses as sites, their contact persons as people.',
  'import.block.items': 'Items',
  'import.block.itemsHint': 'Items onto the catalog by manufacturer and part number, or by SKU, with their accounts from block 1.',
  'import.block.documents': 'Documents',
  'import.block.documentsHint': 'Estimates, sales orders, invoices, credit notes, payments, purchase orders, bills, vendor credits, expenses and journals dated inside a range, each written through its own document. An estimate, a sales order and a purchase order post nothing, and the rest post as Zoho posted it.',
  'import.block.needsContactsAndItems': 'Write the contacts and the items first; a document’s contact and items are found through them.',
  'import.range.from': 'From',
  'import.range.to': 'To',
  'import.range.needed': 'Enter a From and To date, then Read.',
  'import.range.label': 'Range:',
  'import.range.joiner': 'to',
  'import.scope.kind': 'Kind',
  'import.scope.everyKind': 'Every kind',
  'import.scope.everyRecord': 'Every record',
  'import.scope.record': 'One record, by Zoho id',
  'import.scope.recordHint': 'Blank for all',
  'import.scope.changedFrom': 'Changed from',
  'import.scope.changedTo': 'Changed to',
  'import.scope.rangeBoth': 'Choose both days of the range, or neither.',
  'import.scope.label': 'Scope:',
  'import.scope.kind.account': 'Accounts',
  'import.scope.kind.tax': 'Taxes',
  'import.scope.kind.contact': 'Contacts',
  'import.scope.kind.item': 'Items',
  'import.scope.kind.estimate': 'Estimates',
  'import.scope.kind.salesOrder': 'Sales orders',
  'import.scope.kind.invoice': 'Invoices',
  'import.scope.kind.creditNote': 'Credit notes',
  'import.scope.kind.customerPayment': 'Customer payments',
  'import.scope.kind.purchaseOrder': 'Purchase orders',
  'import.scope.kind.bill': 'Bills',
  'import.scope.kind.vendorCredit': 'Vendor credits',
  'import.scope.kind.vendorPayment': 'Vendor payments',
  'import.scope.kind.expense': 'Expenses',
  'import.scope.kind.manualJournal': 'Manual journals',
  'import.scope.kind.inventoryAdjustment': 'Inventory adjustments',
  'import.documents.goLive': 'The ledger opens on',
  'import.documents.pulledThrough': 'Documents are written through',
  'import.compare.title': 'Compare the reports',
  'import.compare.hint': 'Our Profit and Loss and Balance Sheet for the last range pulled, beside Zoho Books’ own, section by section. The rows that differ come first. Comparing reads both and writes nothing.',
  'import.compare.rangeHint': 'Name any range from the day the ledger opens to the day documents are written through. It opens on the last range pulled.',
  'import.compare.rangeNeeded': 'Name a From and a To first.',
  'import.compare.accountsHint': 'Press a section to open its accounts, ours beside Zoho Books’ own, the accounts that differ first. An account one side does not name is blank on that side.',
  // 🔒 T3484: Zoho's answers are kept for the screen's download, and the comparison names the node each section was read from.
  'import.compare.download': 'Download Zoho Books Answers',
  'import.compare.downloadHint': 'Zoho Books’ own answer to each report, as it came, so a reading that looks wrong can be checked against the tree.',
  'import.compare.downloadFailed': 'The browser could not save the file; try the download again.',
  'import.compare.notice.read': 'Zoho Books’ {report} was read as: {sections}',
  // T3531: the path a computed Net Profit/Loss is read from, since Zoho's tree holds no node for it.
  'import.compare.notice.computedPath': 'computed from the sections above',
  'import.compare.notice.readSection': '{section} {figure} from {path}',
  'import.balanceSheet.noOpening': 'Retained Earnings read $0.00 because no opening entry stands for this range; the Import step {step} posts it.',
  'import.compare.run': 'Compare with Zoho Books',
  'import.compare.running': 'Reading Both Statements…',
  'import.compare.none': 'Write a range of documents on block 4 first; the comparison reads the range written.',
  'import.compare.col.report': 'Statement',
  'import.compare.col.section': 'Section',
  'import.compare.col.ours': 'Ours',
  'import.compare.col.zoho': 'Zoho Books',
  'import.compare.col.difference': 'Difference',
  'import.compare.col.verdict': 'Agrees?',
  'import.compare.report.profitAndLoss': 'Profit and Loss',
  'import.compare.report.balanceSheet': 'Balance Sheet',
  'import.compare.section.operatingIncome': 'Operating income',
  'import.compare.section.costOfGoodsSold': 'Cost of goods sold',
  'import.compare.section.operatingExpense': 'Operating expense',
  'import.compare.section.otherIncome': 'Other income',
  'import.compare.section.otherExpense': 'Other expense',
  'import.compare.section.netProfit': 'Net profit',
  'import.compare.section.assets': 'Total assets',
  'import.compare.section.liabilities': 'Total liabilities',
  'import.compare.section.equity': 'Total equity',
  'import.compare.missing': 'none',
  'import.compare.agrees': 'Agrees',
  'import.compare.differs': 'Differs',
  'import.compare.signDiffers': 'Sign differs',
  'import.compare.signDiffersHint': 'A section marked Sign differs carries opposite signs on the two sides. Check which way each side books it before reading the amount as a difference.',
  'import.compare.summary': 'Sections that differ:',
  // 🔒 T3345: Zoho's Total equity stands as Zoho gave it, and the comparison says where it disagrees with Zoho's own assets less liabilities.
  'import.compare.notice.equityDiffers': 'Zoho Books’ Total equity ({equity}) does not equal its Total assets less Total liabilities ({computed}); the difference is {difference}',
  'import.compare.notice.equityMissing': 'Zoho Books answered no Equities section; its Total assets less Total liabilities is {computed}',
  'import.documents.notOpen': 'Nothing is written yet. The first read lists the opening balances first, from Zoho’s balance sheet the day before its range, and writing that row opens the ledger.',
  // Decision 3482: Reverse the Opening Entry on the Documents block, and its window.
  'import.opening.reverse': 'Reverse the Opening Entry',
  'import.opening.reverseTitle': 'Reverse the Opening Entry',
  'import.opening.entry': 'Opening entry',
  'import.opening.nothingToPost': 'none, the ledger opened with nothing to post',
  'import.opening.cutoff': 'Cut-off',
  'import.opening.documents': 'Open documents it voids',
  'import.opening.reverseLead': 'The opening entry and each open document it loaded are reversed on the cut-off day, and each of those documents is voided. Nothing posted since may stand. The next documents read opens the ledger the day before its own range.',
  'import.opening.reason': 'Reason',
  'import.opening.reasonHint': 'Why the go-live date moves',
  'import.opening.reasonNeeded': 'Give the reason first.',
  'import.opening.reversed': 'The opening is reversed. The next documents read opens the ledger the day before its own range.',
  'import.block.lastRun': 'Last run:',
  'import.block.neverRun': 'Not run yet.',
  'import.block.busy': 'A read or a write is in progress; wait for it to finish.',
  'import.block.held': 'This block’s last read is waiting to be written; write it, refresh it or close it first.',
  // Decision 1490: the block's controls drawn as the flow its run takes, Read, then the fork, then the ways out.
  'import.flow.label': 'The steps of this block’s run',
  'import.flow.or': 'or',
  'import.flow.noRead': 'No read is waiting; read the block first.',
  'import.block.needsAccounts': 'Write the chart of accounts and taxes first; an item’s accounts are found through it.',
  'import.read': 'Read',
  'import.writeAll': 'Write All',
  'import.refresh': 'Refresh',
  'import.reading': 'Reading',
  'import.writing': 'Writing the rows the read listed…',
  'import.block.readOn': 'Read on',
  'import.showResult': 'Show Result',
  'import.status.running': 'running',
  'import.status.staged': 'read, waiting to be written',
  'import.status.finished': 'finished',
  'import.status.cancelled': 'canceled',
  'import.status.failed': 'failed',
  'import.pagesRead': 'Pages read:',
  'import.revision.label': 'Software revision:',
  'import.revision.startedEarlier': 'this run started before that revision was deployed, so under an earlier one',
  'import.progress.step1': 'Step 1 · Listing the {many}, page {page}',
  'import.progress.step2': 'Step 2 · Reading each {one}, {read} of {of} on page {page}',
  'import.progress.oneStep': 'Reading the {many}, page {page}',
  'import.progress.oneStepRows': 'Reading the {many}, page {page} · {read} of {of}',
  'import.progress.soFar': '{total} {many} read so far',
  'import.progress.wait': 'Waiting on Zoho’s rate limit, {seconds} s',
  'import.progress.bar': 'This page’s rows read',
  'import.progress.noun.accounts': 'accounts',
  'import.progress.noun.account': 'account',
  'import.progress.noun.contacts': 'contacts',
  'import.progress.noun.contact': 'contact',
  'import.progress.noun.items': 'items',
  'import.progress.noun.item': 'item',
  'import.progress.noun.documents': 'documents',
  'import.progress.noun.document': 'document',
  'import.cancel': 'Cancel',
  'import.cancelling': 'Stopping after this page…',
  'import.failed': 'The run stopped:',
  'import.count.created': 'created',
  'import.count.toCreate': 'to create',
  'import.count.updated': 'updated',
  'import.count.toUpdate': 'to update',
  'import.count.unchanged': 'unchanged',
  'import.count.rejected': 'rejected',
  'import.count.written': 'written',
  'import.count.weakLeft': 'matches left for Step through',
  // Decision 2500: a one-name contact person's question, asked at its row.
  'import.count.askLeft': 'contacts waiting on a one-name question in Step through',
  // Decision 3260: the rows Write all left besides those two, each by its first reason, and the reason most of them share.
  'import.count.heldLeft': 'rows whose Accept is held, left for Step through',
  'import.count.refusedLeft': 'rows refused, left for Step through',
  'import.count.leftReason': 'of the rows left wait for the same reason:',
  'import.oneName.title': 'Who Is This?',
  'import.oneName.lead': 'Zoho Books gives one name for this contact person, and a person here carries a first and a last name. The row waits until you answer.',
  'import.oneName.leadNone': 'Zoho Books gives no name for this contact person, and a person here carries a first and a last name. The row waits until you answer.',
  'import.oneName.gives': 'Zoho Books gives',
  'import.oneName.givesNone': 'Zoho Books gives no name at',
  // T2053: the two name fields start from a guess at the address's name where Zoho Books gives none; a person checks it.
  'import.oneName.guessed': 'Guessed from the email; check it',
  // Decision 6330 (T2913): a name read off the email is a Weak proposal on the staged row, which its Accept writes.
  'import.emailName.read': 'Read the name {name} off {email}; check the split before Accept.',
  'import.emailName.readFirst': 'Read the first name {name} off {email}; the last name waits on its one-name question.',
  'import.oneName.proposed': 'Read off the email:',
  'import.oneName.leadProposed': 'Zoho Books gives no name for this contact person, so the import read one off the email. Accept writes it as shown; change it here first where the split is wrong.',
  'import.oneName.at': 'at',
  'import.oneName.choice': 'What to do',
  'import.oneName.choice.name': 'Type the other name',
  'import.oneName.choice.person': 'Pick the person here',
  'import.oneName.choice.skip': 'Skip them',
  'import.oneName.firstName': 'First name',
  'import.oneName.lastName': 'Last name',
  'import.oneName.search': 'Person here, by name or email',
  'import.oneName.find': 'Find',
  'import.oneName.pick': 'Choose the person',
  'import.oneName.save': 'Save Answer',
  'import.oneName.later': 'Not Now',
  'import.oneName.needsName': 'Type both names first.',
  'import.oneName.needsPerson': 'Find and choose the person first.',
  'import.oneName.noMatch': 'No person here matches; try one name or the email',
  'import.oneName.unwritten': 'typed on row {n}, not written yet; accept that row first',
  'import.oneName.heading': 'People Zoho Books gives one name',
  'import.oneName.waiting': 'waiting for an answer',
  'import.oneName.answer': 'Answer',
  'import.oneName.change': 'Change Answer',
  'import.oneName.answered.name': 'written as',
  'import.oneName.answered.person': 'is',
  'import.oneName.answered.skip': 'skipped',
  // Decision 2973 (T2052): a one-name contact person who is a company's mailbox, not a person.
  'import.oneName.choice.mailbox': 'A company mailbox',
  'import.oneName.mailboxKind': 'What the mailbox is for',
  'import.oneName.mailboxLead': 'The address is written on the company as a mailbox, and no person is written for it.',
  'import.oneName.mailboxGuessed': 'Guessed from the address; check it',
  'import.oneName.answered.mailbox': 'written as the company’s {kind} mailbox',
  // Decision 2502: a possible same-person pair across two contacts, offered as a merge inside the import.
  'import.flag.samePerson': 'Same Person?',
  'import.samePerson.heading': 'People who might be one person',
  'import.samePerson.lead': 'Merge makes the two one person: whichever row is written second joins the person the first wrote. Keep Both writes them as two.',
  'import.samePerson.merge': 'Merge',
  'import.samePerson.keepBoth': 'Keep Both',
  'import.samePerson.merged': 'merged in this import',
  'import.samePerson.kept': 'kept as two people',
  'import.samePerson.open': 'waiting for Merge or Keep Both',
  // Decision 2970: an unanswered pair holds its row's Accept, Edit and Merge, and Step through stops on it.
  'import.flag.samePersonWaiting': 'Same Person? Unanswered',
  'import.samePerson.windowLead': 'Two contacts in this pull name a person of the same name. The row waits until you answer.',
  // Decision 3083 (T2237): the two people side by side, one column each headed by its contact.
  'import.samePerson.field': 'Field',
  'import.samePerson.line.title': 'Title',
  'import.samePerson.line.email': 'Email',
  'import.samePerson.line.phone': 'Phone',
  'import.samePerson.line.mobile': 'Mobile',
  'import.samePerson.line.isPrimary': 'Primary person',
  'import.samePerson.line.contactName': 'Contact',
  'import.samePerson.line.companyName': 'Company name',
  'import.samePerson.line.kind': 'Customer or vendor',
  'import.samePerson.line.contactPhone': 'Contact phone',
  'import.samePerson.line.address': 'Address',
  'import.samePerson.kind.customer': 'Customer',
  'import.samePerson.kind.vendor': 'Vendor',
  'import.samePerson.kind.customerAndVendor': 'Customer and vendor',
  'import.samePerson.yes': 'Yes',
  'import.samePerson.no': 'No',
  'import.samePerson.none': 'none',
  'import.samePerson.differs': 'differs',
  'import.samePerson.ownContact': 'their own contact',
  'import.samePerson.onOwnContact': 'on their own contact',
  'import.samePerson.showColumns': 'Show the two side by side',
  'import.samePerson.mergeMeans': 'Merge writes one person, {name}, employed at {mine} and at {twin}.',
  'import.samePerson.keepMeans': 'Keep Both writes two people: {name} at {mine} and {twinName} at {twin}.',
  'import.samePerson.olderPull': 'This pull was read before the import kept each person’s details, so the two cannot be shown side by side. Refresh the run to see them.',
  'import.stepped.pairWaitingCount': 'rows wait on the same-person question; answer Merge or Keep Both on each first.',
  'import.stepped.namesDifferCount': 'rows were left: each matches a company of another name, so read its reason and accept it by hand.',
  // 🔒 Decision 7980 (T3184): the three passes of a Contacts read, the company joins first, then the people, then the write.
  'import.stepped.companyPairWaitingCount': 'rows were left: each waits on its company pair, Join or Keep Apart.',
  'import.passes.label': 'Passes of this read',
  'import.passes.pass.companies': 'Companies',
  'import.passes.pass.people': 'People',
  'import.passes.pass.write': 'Write',
  'import.passes.left': 'left',
  'import.passes.onRow': 'Decided in three passes; now in',
  'import.passes.companiesLead': 'Each row below joins a company here or one this read creates. Join keeps the two as one company and adds the row’s sites and people to it. Keep Apart makes the row its own company.',
  'import.passes.companiesContinueHint': 'Continue leaves every pair nobody answered as Keep Apart, so two companies that are one stay two until the Duplicates page merges them.',
  'import.passes.peopleLead': 'Each line is a person the read could not place: a contact person giving one name, or a person two contacts may share. Answer each here.',
  'import.passes.peopleContinueHint': 'Continue moves on to the write, and a question left unanswered still holds its row’s Accept.',
  'import.passes.continue': 'Continue',
  'import.passes.join': 'Join',
  'import.passes.keepApart': 'Keep Apart',
  'import.passes.answer': 'Answer',
  'import.passes.col.joins': 'Joins',
  'import.passes.col.rule': 'Rule',
  'import.passes.col.answer': 'Answer',
  'import.passes.col.person': 'Person',
  'import.passes.col.question': 'Question',
  'import.passes.answer.join': 'Joined',
  'import.passes.answer.keepApart': 'Kept Apart',
  'import.passes.answer.waiting': 'Unanswered',
  'import.passes.answer.decided': 'Written',
  'import.passes.answer.skipped': 'Skipped',
  'import.passes.joinsInto': 'joins',
  'import.passes.namesDiffer': 'The names differ once folded:',
  'import.passes.namesAnd': 'and',
  'import.passes.pickPair': 'Pick a pair from the list to answer it.',
  'import.passes.sortLocked': 'The pairs stand in the pass’s order: the names that differ first, then the order the read met them.',
  'import.passes.noPairs': 'No row of this read joins a company.',
  'import.passes.noPeople': 'No person on this read waits on a question.',
  'import.passes.question.oneName': 'Gives One Name',
  'import.passes.decided': 'This row is decided, so its answer stands; reopen the row in the write to change it.',
  'import.passes.writeAllCompanies': 'Answer the Companies pass first: Join or Keep Apart each pair, or press Continue in Step through.',
  'import.passes.peopleAfterCompanies': 'Answer the Companies pass first: Join or Keep Apart each pair, or Continue past it; the People pass follows it.',
  'import.passes.writeAllPeople': 'Answer the People pass first, or press Continue in Step through.',
  'import.overwroteLocal': 'This run replaced records changed here with Zoho Books’ copy.',
  'import.merge.ein': 'merged on the tax number',
  'import.merge.emailDomain': 'merged on a website domain',
  'import.merge.namePostal': 'merged on name and postal code',
  'import.merge.unmatched': 'left unmatched',
  // 🔒 Decision 1923: the buildings the contacts write asked the address verifier about, by its answer.
  'import.addresses.label': 'Addresses',
  'import.addresses.verified': 'verified',
  'import.addresses.needsReview': 'need review',
  'import.addresses.notAsked': 'not asked',
  'import.addresses.noVerifier': 'no address verifier answered, so none was verified; set one up in Settings › Integrations › Maps, or verify each from its location',
  // 🔒 Decision 1514: the manufacturers block 3 creates as companies, by count and name.
  'import.manufacturers.toCreate': 'Manufacturers the write creates',
  'import.manufacturers.created': 'Manufacturers created',
  'import.diff': 'Open the report',
  // 🔒 Decision 1675: an items read's key names of one item, from each service, never a value.
  'import.keys.title': 'What Zoho sent for one item',
  'import.keys.hint': 'The field names of one item read whole from Zoho Books and from Zoho Inventory, so the item mappings are set from what Zoho actually sends. Only the names are kept, never a value.',
  'import.keys.booksItem': 'Zoho Books',
  'import.keys.inventoryItem': 'Zoho Inventory',
  'import.keys.refused': 'Not read:',
  'import.keys.none': 'The answer carried no fields.',
  // 🔒 Decision 1676: an item's details beside its converted fields on Step through.
  'import.stepped.details': 'What else Zoho gives for this item',
  'import.stepped.col.detail': 'Detail',
  'import.stepped.col.value': 'Value',
  // 🔒 Decision 8100: a contact's note and each person's department beside its converted fields, and the note the write adds to the company.
  'import.stepped.contactDetails': 'What else Zoho gives for this contact',
  'import.stepped.detail.note': 'Note, added to the company',
  'import.stepped.detail.departmentOf': 'department',
  'import.stepped.detail.noDepartment': 'no match here',
  'import.zohoNote.heading': 'From Zoho Books',
  'import.zohoNote.department': 'department in Zoho:',
  'import.zohoNote.added': 'a note from Zoho Books is added to the company',
  // 🔒 Decision 2000: the Item import's Uncategorized report and its acknowledgement, on the Items step, the run's result and Sign off.
  // 🔒 Confirm Stock on the Items step (decision 8260): today's counts from Zoho beside ours, writing nothing.
  'import.stock.confirm': 'Confirm Stock',
  // The Dry Run and Run buttons' words, which Start the Documents Again reads (decision 10470); the catch-up cards they were written for retired under decision 10763.
  'import.catchUp.dryRun': 'Dry Run',
  'import.catchUp.run': 'Run',
  'import.catchUp.working': 'Working…',
  'import.catchUp.needsDryRun': 'Press Dry Run first to see what the run posts.',
  'import.catchUp.alreadyRun': 'The run is done; a dry run shows anything left.',
  // 🔒 Decision 10470: Start the Documents Again on Settings › Integrations › Zoho Books, before Sign off and behind a backup.
  'import.startAgain.title': 'Start the Documents Again',
  'import.startAgain.hint': 'Before Sign off, this removes every document, entry, bank line and stock movement the Zoho reads wrote, then you read them again. Companies, people, items, bank accounts and the chart stay. A backup is taken first. Press Dry Run first.',
  'import.startAgain.removes': 'Removes:',
  'import.startAgain.removed': 'Removed:',
  'import.startAgain.keeps': 'Keeps:',
  'import.startAgain.nothing': 'Nothing to remove: no document the Zoho reads wrote stands.',
  'import.startAgain.blockersLead': 'These refuse the run. Clear each, then press Dry Run again:',
  'import.startAgain.blocked': 'Clear what the dry run lists first.',
  'import.startAgain.subject': 'everything the Zoho reads wrote on the document side, after a backup:',
  'import.startAgain.backupAt': 'The backup was taken at',
  'import.startAgain.backupTo': 'to',
  'import.startAgain.proof': 'Now read the documents and the bank transactions again on the Import page.',
  'import.startAgain.kind.documents': 'Documents',
  'import.startAgain.kind.quotes': 'Quotes',
  'import.startAgain.kind.salesOrders': 'Sales Orders',
  'import.startAgain.kind.purchaseOrders': 'Purchase Orders',
  'import.startAgain.kind.invoices': 'Invoices',
  'import.startAgain.kind.creditNotes': 'Credit Notes',
  'import.startAgain.kind.payments': 'Payments',
  'import.startAgain.kind.bills': 'Bills',
  'import.startAgain.kind.vendorCredits': 'Vendor Credits',
  'import.startAgain.kind.journalEntries': 'Journal Entries',
  'import.startAgain.kind.journalLines': 'Journal Lines',
  'import.startAgain.kind.bankLines': 'Bank Lines',
  'import.startAgain.kind.stockMovements': 'Stock Movements',
  'import.startAgain.kind.attachments': 'Attachments',
  'import.startAgain.kind.sourceRefs': 'Zoho References',
  'import.startAgain.kind.runs': 'Document and Bank Reads',
  'import.startAgain.kind.companies': 'Companies',
  'import.startAgain.kind.people': 'People',
  'import.startAgain.kind.sites': 'Sites',
  'import.startAgain.kind.employments': 'Employments',
  'import.startAgain.kind.items': 'Items',
  'import.startAgain.kind.bankAccounts': 'Bank Accounts',
  'import.startAgain.kind.accounts': 'Accounts',
  'import.startAgain.blocker.signedOff': 'Sign off is done, so the documents are the books now.',
  'import.startAgain.blocker.readInFlight': 'A read is running or waiting to be written.',
  'import.startAgain.blocker.closedPeriod': 'An entry it removes is dated in a Closed period.',
  'import.startAgain.blocker.refund': 'A refund stands on an imported payment.',
  'import.startAgain.blocker.taxFiling': 'A sales tax filing stands on an imported payment or invoice.',
  'import.startAgain.blocker.reconciliation': 'A completed reconciliation cleared a Zoho bank line.',
  'import.startAgain.blocker.deposit': 'A deposit holds an imported payment.',
  'import.startAgain.blocker.handApplied': 'A payment applied by hand joins an imported document.',
  'import.startAgain.blocker.note': 'A note stands on an imported document.',
  'import.startAgain.blocker.linked': 'Another record points at an imported document.',
  'import.stock.hint': 'Reads today’s stock on hand for every active Zoho item beside the count here, and writes nothing. Press it after the Items read and after each documents read.',
  'import.stock.reading': 'Reading today’s stock from Zoho…',
  'import.stock.title': 'Stock Check',
  'import.stock.none': 'Zoho lists no active item that holds stock.',
  'import.stock.col.item': 'Item',
  'import.stock.col.zoho': 'Zoho’s Count',
  'import.stock.col.here': 'Count Here',
  'import.stock.col.difference': 'Difference',
  'import.stock.col.written': 'Row Written',
  'import.stock.col.state': 'Standing',
  'import.stock.state.differs': 'Differs',
  'import.stock.state.missing': 'Missing',
  'import.stock.state.notCounted': 'Not Counted',
  'import.stock.state.agrees': 'Agrees',
  'import.stock.written.yes': 'Yes',
  'import.stock.written.no': 'No',
  'import.stock.total.read': 'Items read',
  'import.stock.total.here': 'items here',
  'import.stock.total.agreeing': 'agreeing',
  'import.stock.total.differing': 'differing',
  'import.stock.total.missing': 'missing',
  'import.uncategorized.title': 'Uncategorized report',
  'import.uncategorized.hint': 'Every item a Zoho Books pull brought in that stands in Uncategorized, with the reason no Category rule placed it. Press a row to work it.',
  'import.uncategorized.onCard': 'Uncategorized report, items:',
  'import.uncategorized.open': 'Open the Uncategorized Report',
  'import.uncategorized.written': 'Written on',
  'import.uncategorized.none': 'No item stands in Uncategorized.',
  'import.uncategorized.acknowledge': 'Acknowledge the Uncategorized Report',
  'import.uncategorized.acknowledgedBy': 'Acknowledged by',
  'import.uncategorized.acknowledging': 'Saving the acknowledgement.',
  'import.uncategorized.done': 'The Uncategorized report is acknowledged.',
  'import.uncategorized.already': 'This report is already acknowledged.',
  'import.uncategorized.ackFirst': 'Acknowledge the Uncategorized report first; go-live is signed off against it.',
  'import.uncategorized.noReport': 'No Uncategorized report is written yet; write the items block and its end writes one.',
  'import.uncategorized.itemsBusy': 'An Items read is running or waiting to be written; write or close it, then acknowledge the report its end writes.',
  'import.uncategorized.fixInZoho': 'Give the item a category in Zoho Books and map that value under Category rules, then press Read This One Again.',
  'import.uncategorized.col.item': 'Item',
  'import.uncategorized.col.sku': 'SKU',
  'import.rejected.title': 'Rejected rows',
  'import.rejected.none': 'No row was rejected.',
  'import.rejected.hint': 'Press a row to read its reason in full and work it.',
  'import.rejected.readAgain': 'Read This One Again',
  'import.rejected.openInZoho': 'Open in Zoho Books',
  'import.leftInZoho.title': 'Files in Zoho Books',
  'import.leftInZoho.hint': 'Zoho Books keeps these files and offers no read of them, so they were not brought here; open the record there to see each.',
  'import.rejected.fixInZoho': 'Fix the record in Zoho Books, then press Read This One Again.',
  'import.rejected.fixBlock': 'This kind of row is read with its block; fix it in Zoho Books, then read the block again.',
  'import.rejected.answer.unit': 'The page answers this one itself: match the unit under Units to match, then press Read This One Again.',
  'import.rejected.answer.manufacturer': 'The page answers this one itself: the write creates the manufacturer as a company.',
  'import.rejected.answer.duplicate': 'The page answers this one itself: decide the possible duplicate in Step through, or on the cleanup list once the run is closed.',
  'import.rejected.go.unit': 'Go to Units to Match',
  'import.rejected.go.manufacturer': 'Go to the Manufacturers',
  'import.rejected.go.cleanup': 'Go to the Cleanup List',
  // 🔒 Decision 2030: the opening row's accounts no pull linked, each placed with Match or Create.
  'import.opening.title': 'Accounts no pull linked',
  'import.opening.hint': 'Zoho Books’ balance sheet names these accounts, and none is linked to an account here. Match each to an account here, or create it as the Accounts step would. Once every one is placed, the row and the documents below it are read again.',
  'import.opening.match': 'Match',
  'import.opening.create': 'Create',
  'import.opening.find': 'Find an account here by name or number',
  'import.opening.pick': 'Pick the account',
  'import.opening.pickFirst': 'Find and pick the account here first.',
  'import.opening.placed': 'Placed on',
  'import.opening.closed': 'The read is closed or being written; read this range again to place its accounts.',
  'import.opening.assets': 'Assets',
  'import.opening.liabilities': 'Liabilities',
  'import.opening.equity': 'Equity',
  // 🔒 Decision 4020: a document row waiting on an item offers Create the Item Here and Make It a Text Line.
  'import.waiting.title': 'Lines waiting on an item',
  'import.waiting.hint': 'Create the item here from its row in the Items read, match it to an item here, or write its lines as text lines with no item. A text line never gains the item later, and the item’s own row stays undecided. Every other undecided row of this run waiting on the item takes the same choice.',
  'import.waiting.item': 'Waiting on',
  'import.waiting.itemRow': 'block 3, row',
  'import.waiting.allLines': 'items wait on this document; each is decided in turn, the one named here first.',
  'import.waiting.createItem': 'Create the Item Here',
  'import.waiting.matchItem': 'Match to an Item Here',
  'import.waiting.matchFind': 'Find an item here by SKU or name',
  'import.waiting.matchPick': 'Item here',
  'import.waiting.matchNone': 'No item here answers that search.',
  'import.waiting.matchPickFirst': 'Find and pick the item here first.',
  'import.waiting.textLine': 'Make It a Text Line',
  'import.waiting.textLines': 'Text lines, carrying no item:',
  'import.waiting.chosenBy': 'chosen by',
  'import.waiting.undo': 'Undo the Text Lines',
  'import.waiting.created': 'The item was created here, and this row was read again.',
  'import.waiting.matched': 'The item was matched to the item here, and this row was read again.',
  'import.waiting.made': 'The item’s lines are text lines now, and this row was read again.',
  'import.waiting.followers': 'other rows of this run took the same choice for',
  'import.waiting.followed': 'took the choice made on row',
  'import.waiting.undone': 'The lines wait on their items again.',
  // 🔒 Decision 7350: Read Items Again on a document waiting on an item whose Items read is closed.
  'import.waiting.readItems': 'Read Items Again',
  'import.waiting.readItemsHint': 'Its item stands in an Items read that is closed. Read Items Again reads that item from Zoho Books, converts this run’s undecided rows again and returns to this row.',
  'import.waiting.readHeld': 'Block 3 holds a read waiting to be written; write it or close it first, then read the items again.',
  'import.waiting.readHeldOpen': 'Open That Read',
  'import.waiting.readBusy': 'A read or a write is in progress; Read Items Again opens when it ends.',
  // 🔒 Decision 7350: Pick the Invoice Line on a credit note whose line matches no line of the invoice it credits.
  'import.credit.title': 'Credit lines matching no invoice line',
  'import.credit.hint': 'Pick the line of the invoice each of these credit lines credits. This row converts again with your pick, and Accept opens once every line credits a line.',
  'import.credit.invoice': 'Credits invoice',
  'import.credit.notHere': 'The invoice it credits is not written here yet. Accept its row first, then pick the lines.',
  'import.credit.openInvoice': 'Open the Invoice’s Row',
  'import.credit.line': 'Credit line',
  'import.credit.quantity': 'quantity',
  'import.credit.pickLabel': 'Invoice line',
  'import.credit.pick': 'Pick the Invoice Line',
  'import.credit.pickFirst': 'Choose the invoice line first.',
  'import.credit.left': 'left',
  'import.credit.credits': 'credits invoice line',
  'import.credit.pickedBy': 'picked by',
  'import.credit.picked': 'The invoice line was picked, and this row was read again.',
  'import.stepped.textLined': 'with text lines',
  // 🔒 Decision 2031: a document refused for its opening row opens that row from its reason.
  'import.rejected.openRow': 'Open',
  'import.rows.title': 'Every row',
  'import.rows.show': 'Show Every Row',
  'import.rows.hide': 'Hide Every Row',
  'import.rows.none': 'The run read no rows.',
  'import.col.row': 'Row',
  'import.col.reason': 'Reason',
  'import.col.target': 'Record Here',
  'import.col.outcome': 'Outcome',
  'import.col.detail': 'Notes',
  'import.col.flags': 'Flags',
  'import.flag.possibleDuplicate': 'Possible duplicate',
  'import.outcome.created': 'Created',
  'import.outcome.updated': 'Updated',
  'import.outcome.unchanged': 'Unchanged',
  'import.outcome.rejected': 'Rejected',
  'import.customField': 'Custom field:',
  'import.units.title': 'Units to match',
  'import.units.hint': 'Each unit text below matched no unit here. Match it once and every item carrying it maps without asking again, and the read’s undecided rows carrying it convert again.',
  'import.units.for': 'Unit for',
  'import.units.blank': 'Unit for items that carry none',
  'import.units.pick': 'Choose a unit',
  'import.units.match': 'Match',
  'import.units.done': 'Matched',
  'import.units.pickFirst': 'Choose a unit first.',
  'import.units.matched': 'The unit is matched, and the read’s undecided rows carrying it are converted again.',
  'settings.zohoBooks.title': 'Zoho Books',
  // T731: the page is five numbered steps, each opening once the one before it is done.
  'settings.zohoBooks.hint': 'Five steps connect the Zoho Books importer to your Zoho account. Each step opens once what it needs is saved, and a done step opens again when you press it.',
  'settings.zohoBooks.stepLaterReason': 'Finish the steps above this one first.',
  'settings.zohoBooks.register.title': 'Register this application in Zoho',
  'settings.zohoBooks.register.console': 'Open the Zoho Developer Console',
  'settings.zohoBooks.register.consoleHint': 'The console opens in a new tab, and its own sign-in decides which Zoho account the application is registered under.',
  'settings.zohoBooks.register.needsCentre': 'Choose the data center your Zoho Books account is in, and the link to its console appears here.',
  'settings.zohoBooks.register.clientName': 'Client Name',
  'settings.zohoBooks.register.homepage': 'Homepage URL',
  'settings.zohoBooks.register.save': 'Save the Data Center',
  'settings.zohoBooks.register.summary': 'Data center:',
  'settings.zohoBooks.client.title': 'Paste what Zoho gave you',
  'settings.zohoBooks.client.save': 'Save the Client',
  'settings.zohoBooks.client.summary': 'Client ID:',
  'settings.zohoBooks.organization.title': 'Name the organization',
  'settings.zohoBooks.organization.save': 'Save the Organization',
  'settings.zohoBooks.organization.summary': 'Organization ID:',
  'settings.zohoBooks.allow.title': 'Allow access',
  'settings.zohoBooks.allow.body': 'There is no token to paste: Connect with Zoho opens Zoho’s permission page in this tab, and the token Zoho issues is stored here, sealed, and never shown.',
  'settings.zohoBooks.check.title': 'Check it',
  'settings.zohoBooks.check.body': 'Test connection asks Zoho for the organization’s name, as the Import page does.',
  'settings.zohoBooks.clientId': 'Client ID',
  'settings.zohoBooks.clientIdHint': 'The Client ID Zoho showed when you pressed Create in step 1.',
  'settings.zohoBooks.clientSecret': 'Client secret',
  'settings.zohoBooks.clientSecretHint': 'The Client Secret shown beside it. It is held sealed and never shown again.',
  // Decision 1465: Connect with Zoho, the Server-based client's round trip, in place of the pasted refresh token.
  'settings.zohoBooks.console': 'In the console choose Server-based Applications, enter the three values below and press Create; Zoho then shows a Client ID and a Client Secret.',
  'settings.zohoBooks.redirectLabel': 'Authorized Redirect URLs',
  'settings.zohoBooks.connected': 'Connected on',
  'settings.zohoBooks.connectedUndated': 'Connected',
  'settings.zohoBooks.notConnected': 'Not connected',
  'settings.zohoBooks.connect': 'Connect with Zoho',
  'settings.zohoBooks.connecting': 'Opening Zoho…',
  // Decision 2141: a connection granted before the reports scope was added reads no balance sheet.
  'settings.zohoBooks.reconnectScope': 'A connection granted before the reports permission was added cannot read the balance sheet; press Disconnect, then Connect with Zoho, to grant it.',
  'settings.zohoBooks.connectNeeds': 'Finish steps 1 to 3 first: the data center, the client ID and secret, and the organization ID.',
  'settings.zohoBooks.disconnect': 'Disconnect',
  'settings.zohoBooks.connectedNotice': 'Connected to Zoho Books.',
  'settings.zohoBooks.refusedNotice': 'Zoho Books did not connect:',
  // ZB2 review, S1: the callback returns a reason code, and the page draws the sentence from this fixed list; only Zoho's own word varies.
  'settings.zohoBooks.refusal.noState': 'Zoho sent no state back. Press Connect with Zoho again.',
  'settings.zohoBooks.refusal.stateUnknown': 'This connection was already used or was not started here. Press Connect with Zoho again.',
  'settings.zohoBooks.refusal.stateLapsed': 'The connection took longer than ten minutes. Press Connect with Zoho again.',
  'settings.zohoBooks.refusal.otherSignIn': 'This connection was started by another sign-in. Press Connect with Zoho again from your own.',
  'settings.zohoBooks.refusal.zohoDenied': 'Zoho refused the connection.',
  'settings.zohoBooks.refusal.noCode': 'Zoho sent no code back. Press Connect with Zoho again.',
  'settings.zohoBooks.refusal.unknownLocation': 'Zoho named a data center this application does not know.',
  'settings.zohoBooks.refusal.noDataCentre': 'Choose and save the data center before connecting.',
  'settings.zohoBooks.refusal.noClient': 'Enter and save the client ID and the client secret before connecting.',
  'settings.zohoBooks.refusal.unreachable': 'Zoho’s sign-in host could not be reached. Press Connect with Zoho again.',
  'settings.zohoBooks.refusal.exchangeRefused': 'Zoho refused to exchange the code for a token.',
  'settings.zohoBooks.refusal.exchangeStatus': 'Zoho’s sign-in host answered with an error.',
  'settings.zohoBooks.refusal.noRefreshToken': 'Zoho sent no refresh token. Press Connect with Zoho again, which asks for offline access.',
  'settings.zohoBooks.refusal.unknown': 'The connection was refused. Press Connect with Zoho again.',
  'settings.zohoBooks.zohoWord': 'Zoho answered:',
  'settings.zohoBooks.disconnectedNotice': 'Disconnected from Zoho Books.',
  'settings.zohoBooks.revokeUnconfirmed': 'Disconnected here, but Zoho did not confirm it revoked the token; remove this application under Connected Apps in your Zoho account as well.',
  'settings.zohoBooks.organizationId': 'Organization ID',
  'settings.zohoBooks.organizationIdHint': 'Shown in Zoho Books under Settings › Organization Profile.',
  'settings.zohoBooks.dataCentre': 'Data center',
  'settings.zohoBooks.dataCentrePick': 'Choose a data center',
  'settings.zohoBooks.openImport': 'Open the Importer',
  // 🔒 Decisions 5720 and 7370 (T2800, T3061): the outside-call log has a page of its own, and Zoho Books keeps a link to it.
  'settings.zohoBooks.callsOpen': 'Open Outside Calls',
  'settings.outsideCalls.title': 'Outside Calls',
  'settings.outsideCalls.cardLink': 'Outside Calls',
  'settings.outsideCalls.detail.title': 'Outside Call',
  'settings.outsideCalls.detail.none': 'The service gave no words with this call.',
  // 🔒 Decision 12420 (T3795): a Zoho call's JSON answer, kept seven days and shown in the row's window.
  'settings.outsideCalls.detail.answer': 'Answer',
  'settings.outsideCalls.detail.answerNone': 'No answer is kept for this call: the log keeps only a JSON answer from Zoho Books, Zoho Inventory or Zoho CRM, never a sign-in exchange, a file or another service’s answer.',
  'settings.outsideCalls.detail.answerLoading': 'Reading the answer…',
  'settings.outsideCalls.detail.answerFailed': 'The answer could not be read.',
  'settings.outsideCalls.export': 'Export',
  'settings.outsideCalls.exporting': 'Reading the answers.',
  'settings.outsideCalls.exportLoading': 'The calls are still loading.',
  'settings.outsideCalls.exportFailed': 'The calls could not be read, so there is nothing to export.',
  'settings.outsideCalls.exportNote': 'The file holds the columns shown and an Answer column, for the rows this filter matches, the newest 500 at most. It carries no key or token; a Zoho call keeps Zoho’s answer, and a refused call keeps the service’s own words.',
  'settings.outsideCalls.exportAnswersFailed': 'The answers could not be read, so no file was written.',
  'settings.zohoBooks.calls.hint': 'Every call this application made to an outside service in the last seven days, newest first. A row keeps no key, token or phone number; a Zoho call keeps Zoho’s answer, and a refused call keeps the service’s own words.',
  'settings.zohoBooks.calls.run': 'Run',
  'settings.zohoBooks.calls.runEvery': 'Every run',
  'settings.zohoBooks.calls.status': 'Status',
  'settings.zohoBooks.calls.statusEvery': 'Every status',
  'settings.zohoBooks.calls.providerFilter': 'Service',
  'settings.zohoBooks.calls.providerEvery': 'Every service',
  'settings.zohoBooks.calls.state.answered': 'Answered',
  'settings.zohoBooks.calls.state.refused': 'Refused',
  'settings.zohoBooks.calls.state.unanswered': 'No answer',
  'settings.zohoBooks.calls.empty': 'No call to an outside service in the last seven days matches this filter.',
  'settings.zohoBooks.calls.shown': 'Showing the newest {shown} of {matched} calls.',
  'settings.zohoBooks.calls.col.when': 'When',
  'settings.zohoBooks.calls.col.provider': 'Service',
  'settings.zohoBooks.calls.col.call': 'Call',
  'settings.zohoBooks.calls.col.status': 'Status',
  'settings.zohoBooks.calls.col.took': 'Took',
  'settings.zohoBooks.calls.col.read': 'Read',
  'settings.zohoBooks.calls.col.run': 'Run',
  'settings.zohoBooks.calls.col.refusal': 'Service Said',
  // Decision 7430: an address lookup's low-confidence answer, kept on its row.
  'settings.zohoBooks.calls.col.note': 'Low Confidence',
  'settings.zohoBooks.calls.col.answer': 'Answer',
  'settings.zohoBooks.calls.provider.zohoBooks': 'Zoho Books',
  'settings.zohoBooks.calls.provider.zohoInventory': 'Zoho Inventory',
  'settings.zohoBooks.calls.provider.zohoCrm': 'Zoho CRM',
  'settings.zohoBooks.calls.provider.zohoExpense': 'Zoho Expense',
  'settings.zohoBooks.calls.provider.zohoAccounts': 'Zoho sign-in',
  'settings.zohoBooks.calls.provider.twilio': 'Twilio',
  'settings.zohoBooks.calls.provider.stripe': 'Stripe',
  'settings.zohoBooks.calls.provider.plaid': 'Plaid',
  'settings.zohoBooks.calls.provider.anthropic': 'Anthropic',
  'settings.zohoBooks.calls.provider.peopleDataLabs': 'People Data Labs',
  'settings.zohoBooks.calls.provider.googleMaps': 'Google Maps',
  'settings.zohoBooks.calls.provider.googleVision': 'Google Cloud Vision',
  'settings.zohoBooks.calls.provider.usps': 'USPS',
  'settings.zohoBooks.calls.provider.firebaseMessaging': 'Firebase Cloud Messaging',
  'settings.zohoBooks.calls.provider.openStreetMap': 'OpenStreetMap',
  'settings.zohoBooks.calls.provider.censusGeocoder': 'Census Bureau geocoder',
  'settings.zohoBooks.calls.provider.geoapify': 'Geoapify',
  'settings.zohoBooks.calls.provider.singleSignOn': 'Single sign-on',
  'settings.zohoBooks.calls.provider.turnstile': 'Turnstile',
  'settings.zohoBooks.calls.noAnswer': 'No answer',
  'settings.zohoBooks.calls.notRead': 'Not read',
  'import.calls.open': 'See This Run’s Calls to Zoho',
  'jobs.purgeOutsideCallLog.label': 'Purge the outside-call log',
  'jobs.purgeOutsideCallLog.purpose': 'Deletes every call to an outside service older than seven days from Settings › Integrations › Outside Calls.',
  // Decision 8040 (T3190): block 5, the bank and card lines over a range.
  'import.block.bankTransactions': 'Bank transactions',
  'import.block.bankTransactionsHint': 'Every bank and credit card account’s lines dated inside a range, each landing on its account’s feed. A line Zoho categorized against a payment is matched to it, one categorized to an account is categorized here, and an uncategorized line waits to be categorized.',
  'import.block.needsDocuments': 'Write a documents range first; a bank line is matched to the payment the documents wrote.',
  'import.steps.bankTransactions': 'Bank transactions by range',
  'import.bank.writtenThrough': 'Documents written through',
  'import.progress.noun.bankLines': 'bank lines',
  'import.progress.noun.bankLine': 'bank line',
  // 🔒 Decision 8380: a Manufacturers read's running line.
  'import.progress.noun.manufacturers': 'manufacturers',
  'import.progress.noun.manufacturer': 'manufacturer',
  'import.progress.noun.expenseReports': 'expense reports',
  'import.progress.noun.expenseReport': 'expense report',
  'import.scope.kind.bankTransaction': 'Bank transactions',
  // 🔒 Decision 9280: a CSV file run's running line, never drawn, since a CSV read is one request.
  'import.progress.noun.csvRows': 'rows',
  'import.progress.noun.csvRow': 'row',
  'import.scope.kind.csvRow': 'CSV rows',
  'import.scope.kind.expenseReport': 'Expense reports',
  'import.csv.entity.companies': 'Companies',
  'import.csv.entity.people': 'People',
  'import.csv.entity.locations': 'Locations',
  'import.csv.entity.items': 'Items',
  'import.csv.entity.companiesHint': 'Each row is a company, matched on its EIN, else its name at its postal code.',
  'import.csv.entity.peopleHint': 'Each row is a person, matched on the work email, else the name at the company the row names. Load the companies first.',
  'import.csv.entity.locationsHint': 'Each row is a building, matched on its address lines, postal code and two-letter country code.',
  'import.csv.entity.itemsHint': 'Each row is an item, matched on its manufacturer and part number.',
  // 🔒 Decision 9280: the CSV File preset's screen.
  'import.preset.csvFile': 'CSV File',
  'import.csv.hint': 'Load one spreadsheet saved as CSV UTF-8, one kind of record a file, at most 5 MB and 10,000 rows. Read writes nothing; every row is listed first.',
  'import.csv.entity.legend': 'The File Holds',
  'import.csv.file.label': 'CSV File',
  'import.csv.sample': 'Download Sample File',
  'import.csv.sampleHint': 'The sample is the header row alone; put one record on each row beneath it.',
  'import.csv.readHold': 'Choose the file to read first.',
  'import.csv.waitingHold': 'A file of this kind is waiting below; write it all or close it first.',
  'import.csv.waiting': 'Read on',
  'import.csv.counts': '{created} to create · {updated} to update · {unchanged} unchanged · {rejected} rejected',
  'import.csv.writtenCounts': '{created} created · {updated} updated · {unchanged} unchanged · {rejected} rejected or skipped',
  'import.csv.lastRun': 'Last run',
  'import.csv.columns.title': 'Column Map',
  'import.csv.columns.ignored': 'Ignored',
  'import.csv.rows.title': 'Rows',
  'import.csv.row': 'Row',
  'import.csv.outcome.create': 'Create',
  'import.csv.outcome.update': 'Update',
  'import.csv.outcome.unchanged': 'Unchanged',
  'import.csv.outcome.reject': 'Rejected',
  'import.csv.written.created': 'Created',
  'import.csv.written.updated': 'Updated',
  'import.csv.written.unchanged': 'Written Unchanged',
  'import.csv.written.skipped': 'Skipped',
  'import.csv.written.refused': 'Refused',
  'import.csv.matched': 'Matches',
  'import.csv.noChanges': 'No cell differs from the record here.',
  'import.csv.failure': 'This run ended with a problem:',
  'import.csv.accept': 'Accept',
  'import.csv.skip': 'Skip',
  'import.csv.writing': 'Writing the rows; the list follows when it ends.',
  'import.csv.nothingLeft': 'Every row is decided or written; nothing is left to write.',
  'import.csv.decideHold': 'Write All is writing this run; wait for it to end.',
} as const;
